import { useNode } from "@craftjs/core";
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";
import { colors } from "../../../../utils";
import { ASSETS } from "../../../../utils/config";
import {
  goToUrl,
  percentageToSlider,
  sliderToPercentage,
} from "../../../../utils/helpers";
import CustomSwitch from "../../../CustomSwitch";
import { handleAiAssistantIcon } from "../../../FroalaEditor/children/FroalaHelpers";
import FroalaTextEditor from "../../../FroalaEditor/children/FroalaTextEditor";
import Input from "../../../Input";
import NewAIPrompt from "../../../NewAiPrompt";
import ColorPopup from "../../../SegmentBuilder/Shared/ColorPopup";
import TextView from "../../../TextView";
import MarginBox from "../../Shared/MarginBox";
import MediaSelection from "../../Shared/MediaSelection";
import SettingsSlider from "../../Shared/SettingsSlider";
import UploadedMedia from "../../Shared/UploadedMedia";
import AlignmentBox from "./AlignmentBox";
import ShareWithBorder from "../../../../assets/icons/ShareWithBorder";
import StyleInfoCard from "../../Main/StyleTab/StyleInfoCard";
import CustomTabs from "../../Shared/CustomTabs";
import { useDispatch, useSelector } from "react-redux";
import { updateImageWithTextStyles } from "../../../../redux/reducers/PrevStylesCopy/CodeblockDefaultStyles";
import { toggleSegmentSettings } from "../../../../redux/actions/segmentSettings";
import { getSelectedSegmentCharCount } from "../../../../redux/selectors/segmentBuilderSelectors";
import { charLimitInASegment } from "../../../SegmentSettings";
import { resolveImageGapPx } from "./imageWrapMargin";
import { isSafeHttpUrl } from "../../../../utils/urlValidation";

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${colors.lightBorder};
  margin-bottom: 20px;
`;

const ImageWithTextSettings = () => {
  const {
    id,
    actions: { setProp },
    width = "50%",
    isApplyMarginOnAllSides = false,
    margin = { top: 5, bottom: 5, left: 10, right: 10 },
    allSideMargin = 5,
    borderRadius = 4,
    borderColor = "suggested_color_0",
    borderWidth = 1,
    src = "",
    title = "",
    showImageTitle = false,
    hyperlink = null,
    showHyperlink = false,
    dropShadowColor = null,
    isLeftAlign = false,
    text = "",
    textPosition = "right",
    originalSrc = "",
    image = {},
    headingProps = {
      fontColor: "black",
      fontFamily: "Poppins",
      fontSize: 16,
      resetKey: 0,
    },
    paragraphProps = {
      fontColor: "black",
      fontFamily: "Poppins",
      fontSize: 14,
      resetKey: 0,
    },
    lineHeight = 1.25,
    imageGap,
  } = useNode((node) => ({
    width: node.data.props.width,
    isApplyMarginOnAllSides: node.data.props.isApplyMarginOnAllSides,
    margin: node.data.props.margin,
    allSideMargin: node.data.props.allSideMargin,
    borderRadius: node.data.props.borderRadius,
    borderWidth: node.data.props.borderWidth,
    borderColor: node.data.props.borderColor,
    src: node.data.props.src,
    title: node.data.props.title,
    showImageTitle: node.data.props.showImageTitle,
    hyperlink: node.data.props.hyperlink,
    showHyperlink: node.data.props.showHyperlink,
    dropShadowColor: node.data.props.dropShadowColor,
    text: node.data.props.text,
    textPosition: node.data.props.textPosition,
    originalSrc: node.data.props.originalSrc || node.data.props.src,
    image: node.data.props.image || {},
    headingProps: node.data.props.headingProps,
    paragraphProps: node.data.props.paragraphProps,
    lineHeight: node.data.props.lineHeight,
    isLoading: node.data.props.isLoading,
    imageGap: node.data.props.imageGap,
  }));

  const dispatch = useDispatch();
  const selectedSegmentCharCount = useSelector(getSelectedSegmentCharCount);
  const [showAiPrompt, setShowAiPrompt] = useState(false);
  const [selectedTab, setSelectedTab] = useState("0");

  const handleAdjustment = (side, operation) => {
    const newValue =
      operation === "increase" ? margin[side] + 1 : margin[side] - 1;
    if (newValue >= 0) handleChange("margin", newValue, side);
  };

  const handleChange = (type, value, side) => {
    dispatch(toggleSegmentSettings(false));
    setProp((props) => {
      // Clone the props to avoid mutating Proxies
      const newProps = {
        ...props,
        margin: { ...props.margin },
        image: { ...props.image },
        headingProps: { ...props.headingProps },
        paragraphProps: { ...props.paragraphProps },
      };

      switch (type) {
        case "width":
          props.width = sliderToPercentage(value);
          newProps.width = sliderToPercentage(value);
          break;
        case "margin":
          if (value >= 0 && value <= 100) {
            props.margin[side] = value;
            newProps.margin[side] = value;
          }
          break;
        case "allSideMargin":
          props.allSideMargin = value;
          newProps.allSideMargin = value;
          break;
        case "marginCheckbox":
          props.isApplyMarginOnAllSides = !props.isApplyMarginOnAllSides;
          newProps.isApplyMarginOnAllSides = !props.isApplyMarginOnAllSides;
          break;
        case "border":
          props.borderRadius = value;
          newProps.borderRadius = value;
          break;
        case "borderColor":
          props.borderColor = value;
          newProps.borderColor = value;
          break;
        case "resetColor":
          props.borderColor = null;
          newProps.borderColor = null;
          break;
        case "borderWidth":
          props.borderWidth = value;
          newProps.borderWidth = value;
          break;
        case "image":
          props.image = { ...props.image, ...value };
          newProps.image = { ...newProps.image, ...value };
          // Set loading state based on the uploaded file data only if loading key exists
          if (value.hasOwnProperty("loading")) {
            props.isLoading = value.loading !== false;
            newProps.isLoading = value.loading !== false;
          }
          break;
        case "title":
          props.image = { ...props.image, title: value };
          newProps.image = { ...newProps.image, title: value };
          break;
        case "showImageTitle":
          props.showImageTitle = !props.showImageTitle;
          newProps.showImageTitle = !props.showImageTitle;
          break;
        case "showHyperlink":
          props.showHyperlink = !props.showHyperlink;
          newProps.showHyperlink = !props.showHyperlink;
          break;
        case "hyperlink":
          props.hyperlink = value;
          newProps.hyperlink = value;
          break;
        case "dropShadowColor":
          props.dropShadowColor = value;
          newProps.dropShadowColor = value;
          break;
        case "resetShadow":
          props.dropShadowColor = null;
          newProps.dropShadowColor = null;
          break;
        case "changeText":
          props.text = value;
          newProps.text = value;
          break;
        case "changeTextAlignment":
          props.textPosition = value;
          newProps.textPosition = value;
          break;
        case "imageRemove":
          props.image = {};
          newProps.image = {};
          break;
        case "lineHeight":
          props.lineHeight = value;
          newProps.lineHeight = value;
          break;
        case "imageGap":
          props.imageGap = value;
          newProps.imageGap = value;
          break;
        case "headingProps":
          if (side === "resetKey") {
            props.headingProps.resetKey = props.headingProps.resetKey + 1;
            newProps.headingProps.resetKey = props.headingProps.resetKey + 1;
          } else {
            props.headingProps[side] = value;
            newProps.headingProps[side] = value;
          }
          break;
        case "paragraphProps":
          if (side === "resetKey") {
            props.paragraphProps.resetKey = props.paragraphProps.resetKey + 1;
            newProps.paragraphProps.resetKey =
              props.paragraphProps.resetKey + 1;
          } else {
            props.paragraphProps[side] = value;
            newProps.paragraphProps[side] = value;
          }
          break;

        default:
          break;
      }

      // Dispatch the updated props to redux
      // Don't include text, image content, and instance-specific settings in the defaults
      const updatedProps = { ...newProps };
      updatedProps.text = ""; // Don't save the actual text content
      if (updatedProps.image && updatedProps.image.src) {
        updatedProps.image = { ...updatedProps.image, src: "", title: "" }; // Don't save the actual image source or title
      }
      // Don't save instance-specific settings that should not be shared across blocks
      delete updatedProps.showImageTitle;
      delete updatedProps.showHyperlink;
      delete updatedProps.hyperlink;
      delete updatedProps.title;
      dispatch(updateImageWithTextStyles(updatedProps));
    }, 500);
  };

  const imageSelected = image?.src || src;
  // Hyperlink validation (parity with the Image block v2 editor): a non-empty
  // link that is not a safe http(s) URL is flagged inline and never navigated to.
  const hyperlinkError =
    showHyperlink && hyperlink && String(hyperlink).trim()
      ? !isSafeHttpUrl(hyperlink)
      : false;
  const editorRef = useRef(null);

  const handleFroalaCustomization = (FroalaEditor) => {
    handleAiAssistantIcon(FroalaEditor, setShowAiPrompt);
  };

  const handleImageChange = (type, value) => {
    handleChange(type, value);
    setProp((props) => {
      props.originalSrc = value;
    });
  };

  const renderLayout = () => {
    return (
      <>
        <AlignmentBox
          isLeftAlignment={textPosition === "right"}
          handleAlignment={(val) => handleChange("changeTextAlignment", val)}
        />

        <Divider />

        <MarginBox
          margin={margin}
          increaseMargin={(side) => handleAdjustment(side, "increase")}
          decreaseMargin={(side) => handleAdjustment(side, "decrease")}
          isApplyMarginOnAllSides={isApplyMarginOnAllSides}
          allSideMargin={allSideMargin}
          handleMarginCheckBox={() => handleChange("marginCheckbox")}
          handleSliderMarginChange={(value) =>
            handleChange("allSideMargin", value)
          }
          handleMarginChange={(side, newValue) =>
            handleChange("margin", newValue, side)
          }
        />

        {imageSelected && (
          <>
            <SettingsSlider
              title="Resizing"
              sliderValue={percentageToSlider(width)}
              onValueChange={(val) => handleChange("width", val)}
              min={10}
              max={100}
              valueLabel={"%"}
            />
            <SettingsSlider
              title="Corner Rounding"
              sliderValue={borderRadius}
              onValueChange={(val) => handleChange("border", val)}
              min={0}
            />

            <SettingsSlider
              title="Image Spacing"
              tooltip="Space between the image and the wrapped text"
              sliderValue={resolveImageGapPx(textPosition, imageGap)}
              onValueChange={(val) => handleChange("imageGap", val)}
              min={0}
              max={40}
            />

            <ColorPopup
              title="Border"
              background={borderColor}
              handleChangeBackgroundColor={(val) =>
                handleChange("borderColor", val)
              }
              resetBackgroundColor={() => handleChange("resetColor")}
              sliderBoxTitle={"Border Width"}
              sliderValue={borderWidth}
              sliderChange={(val) => handleChange("borderWidth", val)}
              sliderMinValue={0}
              sliderMaxValue={8}
              isHideGradient={true}
              sliderSymbol={"px"}
              showSuggestedColorPalette={true}
              isBorderSlider
            />

            <ColorPopup
              title="Drop Shadow"
              background={dropShadowColor}
              handleChangeBackgroundColor={(val) =>
                handleChange("dropShadowColor", val)
              }
              resetBackgroundColor={() => handleChange("resetShadow")}
              showSlider={false}
              showSuggestedColorPalette={true}
            />
          </>
        )}
      </>
    );
  };

  const handleTabChange = (e, newVal) => {
    setSelectedTab(newVal);
  };

  const tabOptions = new Map([
    [
      "0",
      {
        label: `Image`,
        component: (
          <>
            {imageSelected && (
              <UploadedMedia
                type="image"
                media={image?.src || src}
                originalImage={originalSrc?.src}
                title={image?.title || title}
                onChange={(val) => handleChange("title", val)}
                showTitle={showImageTitle}
                handleCheckbox={() => handleChange("showImageTitle")}
                handleEditMedia={(value) => handleChange("image", value)}
                handleMediaRemove={() => handleChange("imageRemove")}
              />
            )}
            <MediaSelection
              type={"image"}
              isGifAllowed
              handleChange={handleImageChange}
              handleTitle={(val) => handleChange("title", val)}
            />

            <TextView
              variant="subtitle"
              style={{ marginBottom: 10, marginTop: 20 }}
            >
              Add Hyperlink to Image{" "}
              <CustomSwitch
                checked={showHyperlink}
                onChange={() => handleChange("showHyperlink")}
              />
            </TextView>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gridGap: 10,
              }}
            >
              <Input
                variant="secondary"
                placeholder="Paste link here"
                value={hyperlink}
                onChange={(e) => handleChange("hyperlink", e.target.value)}
              />
              <ShareWithBorder
                style={{
                  marginTop: 14,
                  cursor: hyperlinkError ? "not-allowed" : "pointer",
                  opacity: hyperlinkError ? 0.4 : 1,
                }}
                onClick={() => {
                  if (!hyperlinkError && hyperlink) goToUrl(hyperlink);
                }}
              />
            </div>
            {hyperlinkError && (
              <TextView
                variant="tag"
                color={"#B5251F"}
                style={{ fontSize: 12, marginTop: 6 }}
              >
                Enter a full link (https://…) or leave it empty.
              </TextView>
            )}
          </>
        ),
      },
    ],
    [
      "1",
      {
        label: "Text",
        component: (
          <>
            <FroalaTextEditor
              editorHeight={"150px"}
              name={`text_body`}
              id={1}
              value={text}
              onChange={(val) => handleChange("changeText", val)}
              handleEditorSetup={() => console.log("Setup")}
              handleEditorCustomization={handleFroalaCustomization}
              editorRef={editorRef}
              placeholder="Add Text here"
              lineHeight={lineHeight}
              headingProps={headingProps}
              paragraphProps={paragraphProps}
              disableAtCharLimit
            />

            {/* Character count indicator */}
            <div
              style={{
                textAlign: "right",
                fontSize: "12px",
                color:
                  selectedSegmentCharCount > charLimitInASegment
                    ? "#ff4d4f"
                    : "#999",
                margin: "5px 16px 10px 16px",
              }}
            >
              {selectedSegmentCharCount > charLimitInASegment
                ? `${charLimitInASegment}/${charLimitInASegment} (Segment character limit exceeded by ${
                    selectedSegmentCharCount - charLimitInASegment
                  } characters)`
                : `${selectedSegmentCharCount}/${charLimitInASegment}`}
            </div>
            <StyleInfoCard
              title={"Heading Text"}
              background="none"
              borderBottom
              applyButton={false}
              selectedFont={headingProps?.fontFamily}
              selectedColor={headingProps?.fontColor}
              handleFontChange={(font) =>
                handleChange("headingProps", font, "fontFamily")
              }
              inputValue={headingProps?.fontSize}
              handleInputValueChange={(size) =>
                handleChange("headingProps", size, "fontSize")
              }
              onConfirmCustomColor={(color) =>
                handleChange("headingProps", color, "fontColor")
              }
              tooltip="Changes the settings for any text in the block that is formatted as a 'Header'"
              handleResetFontColor={() =>
                handleChange("headingProps", null, "resetKey")
              }
              type="Header"
            />

            <StyleInfoCard
              title={"Paragraph Text"}
              background="none"
              borderBottom
              applyButton={false}
              selectedFont={paragraphProps?.fontFamily}
              selectedColor={paragraphProps?.fontColor}
              handleFontChange={(font) =>
                handleChange("paragraphProps", font, "fontFamily")
              }
              inputValue={paragraphProps?.fontSize}
              handleInputValueChange={(size) =>
                handleChange("paragraphProps", size, "fontSize")
              }
              onConfirmCustomColor={(color) =>
                handleChange("paragraphProps", color, "fontColor")
              }
              tooltip="Changes the settings for any text in the block that is formatted as a 'Paragraph'"
              handleResetFontColor={() =>
                handleChange("paragraphProps", null, "resetKey")
              }
              type={"Paragraph"}
            />

            <SettingsSlider
              title="Line Spacing"
              sliderValue={lineHeight}
              onValueChange={(value) => handleChange("lineHeight", value)}
              min={1}
              max={2}
              valueLabel={false}
              step={0.05}
              allowDecimal
            />
          </>
        ),
      },
    ],
    [
      "2",
      {
        label: "Layout",
        disabled: !image?.src,
        component: <div>{renderLayout()}</div>,
      },
    ],
  ]);

  return (
    <>
      <CustomTabs
        selectedTab={selectedTab}
        handleTabChange={handleTabChange}
        options={tabOptions}
      />

      {showAiPrompt &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              zIndex: 6522299,
              right: "35vw",
              top: "25vh",
              overflow: "auto",
            }}
          >
            <NewAIPrompt
              modalWidth="350px"
              conversationKey={id}
              onCloseIconPress={() => setShowAiPrompt(false)}
            />
          </div>,
          document.body // You can also use a different element if needed
        )}
    </>
  );
};

export default ImageWithTextSettings;
